import { GoalReviewStatus, GoalStatus } from '@lmc/ts-common'
import { Chevron, MultiSelect, Select } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import useResidents from 'hooks/useFindItem/useResidents'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { getName } from 'screens/Todos/components/BasicFilters'
import styled from 'styled-components'
import { goalReportsConstants } from '../../../../../locale/goalReports'
import BasicFilters from '../../../MonitoringReports/components/DashboardFilters/BasicFilters'
import type { Filter } from '../../../MonitoringReports/components/DashboardFilters/SelectedFilters'
import type { FilterValues } from '../types'
import { GoalSelectedFilters } from './GoalSelectedFilters'

export type GoalsFiltersProps = {
  filterParams: FilterValues
  setFilterParams: SetFilterParams
  hasOnClick?: boolean
  testId?: string
}
export type SetFilterParams = (f: (filterParams: FilterValues) => void) => void

export const PlaceHolder = (text: string) => (
  <StyledPlaceholder>{text}</StyledPlaceholder>
)

export const GoalFilters = ({
  filterParams,
  setFilterParams,
  testId
}: GoalsFiltersProps) => {
  const { t } = useTranslation(NAMESPACE.GOAL_REPORTS)
  const value = filterParams
  const { all: allResidents } = useResidents({})

  const statusOptions = [
    {
      value: GoalStatus.inProgress,
      label: t(goalReportsConstants[GoalStatus.inProgress])
    },
    {
      value: GoalStatus.paused,
      label: t(goalReportsConstants[GoalStatus.paused])
    },
    {
      value: GoalStatus.notStarted,
      label: t(goalReportsConstants[GoalStatus.notStarted])
    },
    {
      value: GoalStatus.done,
      label: t(goalReportsConstants[GoalStatus.done])
    },
    {
      value: GoalStatus.archive,
      label: t(goalReportsConstants[GoalStatus.archive])
    }
  ]

  const reviewStatusOptions = [
    {
      value: GoalReviewStatus.overdue,
      label: t(goalReportsConstants[GoalReviewStatus.overdue])
    },
    {
      value: GoalReviewStatus.dueToday,
      label: t(goalReportsConstants[GoalReviewStatus.dueToday])
    },
    {
      value: GoalReviewStatus.dueTomorrow,
      label: t(goalReportsConstants[GoalReviewStatus.dueTomorrow])
    },
    {
      value: GoalReviewStatus.dueThisWeek,
      label: t(goalReportsConstants[GoalReviewStatus.dueThisWeek])
    }
  ]

  const mappedSelectedFilters = {
    ...value,
    ...(value.residents && {
      residents: value.residents.map((resident: string) => {
        const residentName = allResidents?.find((r) => r.id === resident)
        return residentName ? getName(residentName) : null
      })
    }),

    ...(value.goalStatuses && {
      goalStatuses: value.goalStatuses.map(
        (status: GoalStatus) =>
          statusOptions?.find((s) => s.value.toString() === status)?.label
      )
    }),

    ...(value.reviewStatuses && {
      reviewStatuses: value.reviewStatuses.map(
        (status: GoalReviewStatus) =>
          reviewStatusOptions?.find((s) => s.value.toString() === status)?.label
      )
    })
  }

  const onFilterChange = (value: Partial<FilterValues>) => {
    const newValue = { ...value }
    setFilterParams((prevFilters: FilterValues) => ({
      ...prevFilters,
      ...newValue
    }))
  }

  return (
    <>
      <BasicFilters
        value={value}
        onChange={onFilterChange}
        withLoggedBy={false}
        hasResidentsFilter
        allowClearDateRange={false}
        hasRangePicker={false}
        variant="highContrast"
        data-testid={testId ? `${testId}-goal-filters` : 'goal-filters'}
      >
        <StyledMultiSelectWide
          options={statusOptions}
          title={t(goalReportsConstants.STATUS)}
          groupedOptions={undefined}
          hasClear
          hasSearch
          hasSelectAll
          onChange={(selectedStatus) => {
            onFilterChange({ goalStatuses: selectedStatus } as FilterValues)
          }}
          variant="highContrast"
          data-testid={
            testId ? `${testId}-goal-filters-status` : 'goal-filters-status'
          }
        />

        <StyledSelect
          placeholder={PlaceHolder(t(goalReportsConstants.REVIEW_STATUS))}
          optionRender={(option) => <Text>{option.label}</Text>}
          options={reviewStatusOptions}
          value={filterParams.reviewStatuses}
          onChange={(selectedStatus) =>
            onFilterChange({ reviewStatuses: selectedStatus } as FilterValues)
          }
          mode="multiple"
          suffixIcon={<Chevron direction="down" />}
          data-testid={
            testId
              ? `${testId}-goal-filters-review-status`
              : 'goal-filters-review-status'
          }
        />
      </BasicFilters>
      {mappedSelectedFilters && (
        <div
          data-testid={
            testId ? `${testId}-goal-selected-filters` : 'goal-selected-filters'
          }
        >
          <GoalSelectedFilters
            selectedFilters={mappedSelectedFilters as Filter}
          />
        </div>
      )}
    </>
  )
}

const StyledSelect = styled(Select)`
  min-width: 200px;

  .ant-select-selector {
    border-color: ${({ theme }) => theme.color.neutral.color5} !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
` as typeof Select

const StyledPlaceholder = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const StyledMultiSelectWide = styled(MultiSelect)`
  min-width: 200px;
` as typeof MultiSelect
